import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { DEFAULT_LANG } from "../../i18n";

import Footer from "../Components/Footer";
import Menubar from "../Components/Menubar";
import Totop from "../Components/Totop";
import "./ClinicalResearch.css";
import "../../index.css";
import { MediaQueryContext } from "../../context";

const renderMultiline = (text) =>
  text.split("\n").map((line, i, arr) => (
    <React.Fragment key={i}>
      {line}
      {i < arr.length - 1 && <br />}
    </React.Fragment>
  ));

const ClinicalResearch = (props) => {
  const { mTablet } = useContext(MediaQueryContext);
  const { t } = useTranslation();
  const history = useHistory();
  const langMatch = useLocation().pathname.match(/^\/(kr|en)(\/|$)/);
  const lang = langMatch ? langMatch[1] : DEFAULT_LANG;
  const goto = (p) => history.push(`/${lang}${p}`);

  const executeScroll = () => {
    const element = document.getElementById(props.location.hashId);
    const headOffset = mTablet ? 84 : 184;
    const elementPosition = element?.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - headOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    executeScroll();
  }, [props.location.update]);

  // DRAFT: 대표님 확인 전 초안. 현재 수행 중인 서비스·실적 기준으로 구성
  const services = [
    { icon: "01", key: "design" },
    { icon: "02", key: "oversight" },
    { icon: "03", key: "analysis" },
  ];
  const flowSteps = ["consult", "design", "oversight", "dm"];
  const digitalStats = ["usability", "usefulness", "patients"];
  const rndItems = ["emr", "ai"];

  return (
    <div id="clinicalresearch">
      <Menubar slideIndex={0} />
      <Totop />
      <div className="cr-main-container">
        {/* Hero */}
        <div className="cr-hero">
          <div className="cr-hero-text">
            <span className="cr-hero-maintext">
              {renderMultiline(t("clinicalresearch.hero.title"))}
            </span>
            <span className="cr-hero-subtext">
              {renderMultiline(t("clinicalresearch.hero.subtitle"))}
            </span>
          </div>
        </div>

        {/* Section 1: 임상 연구 서비스 */}
        <div className="cr-section" id="services">
          <div className="cr-section-header">
            <span className="cr-section-label">{t("clinicalresearch.services.label")}</span>
            <p className="cr-section-title">{t("clinicalresearch.services.title")}</p>
            <p className="cr-section-desc">{t("clinicalresearch.services.desc")}</p>
          </div>
          <div className="cr-features-grid cr-features-3">
            {services.map((item) => (
              <div className="cr-feature-card" key={item.key}>
                <div className="cr-feature-icon">{item.icon}</div>
                <p className="cr-feature-title">
                  {t(`clinicalresearch.services.items.${item.key}.title`)}
                </p>
                <p className="cr-feature-desc">
                  {t(`clinicalresearch.services.items.${item.key}.desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Section 2: 서비스 구조 (리플렛 기준) */}
        <div className="cr-section cr-section-soft" id="flow">
          <div className="cr-section-header">
            <span className="cr-section-label">{t("clinicalresearch.flow.label")}</span>
            <p className="cr-section-title">{t("clinicalresearch.flow.title")}</p>
            <p className="cr-section-desc">{t("clinicalresearch.flow.desc")}</p>
          </div>
          <div className="cr-flow-steps">
            {flowSteps.map((key, idx) => (
              <React.Fragment key={key}>
                <div className={`cr-flow-step ${idx === 0 ? "cr-flow-step-first" : ""}`}>
                  <span className="cr-flow-step-no">{`0${idx + 1}`}</span>
                  <p className="cr-flow-step-title">
                    {t(`clinicalresearch.flow.steps.${key}.title`)}
                  </p>
                  <p className="cr-flow-step-desc">
                    {renderMultiline(t(`clinicalresearch.flow.steps.${key}.desc`))}
                  </p>
                  <p className="cr-flow-step-price">
                    <span>{t("clinicalresearch.flow.priceLabel")}</span>
                    {t(`clinicalresearch.flow.steps.${key}.price`)}
                  </p>
                </div>
                {idx < flowSteps.length - 1 && <span className="cr-flow-caret" aria-hidden="true" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Section 3: 디지털 임상 (NuGa Trial) */}
        <div className="cr-section cr-section-dark" id="digital">
          <div className="cr-section-header">
            <span className="cr-section-label cr-label-light">
              {t("clinicalresearch.digital.label")}
            </span>
            <p className="cr-section-title cr-title-light">
              {renderMultiline(t("clinicalresearch.digital.title"))}
            </p>
            <p className="cr-section-desc cr-desc-light">
              {t("clinicalresearch.digital.desc")}
            </p>
          </div>
          <div className="cr-stats-row">
            {digitalStats.map((key) => (
              <div className="cr-stat-card" key={key}>
                <span className="cr-stat-number">
                  {t(`clinicalresearch.digital.stats.${key}.number`)}
                </span>
                <span className="cr-stat-label">
                  {t(`clinicalresearch.digital.stats.${key}.label`)}
                </span>
                <span className="cr-stat-desc">
                  {t(`clinicalresearch.digital.stats.${key}.desc`)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Section 4: 로드맵(TO-BE) — 수치·효과 표현 없이 */}
        <div className="cr-section" id="rnd">
          <div className="cr-section-header">
            <span className="cr-section-label">{t("clinicalresearch.rnd.label")}</span>
            <p className="cr-section-title">{t("clinicalresearch.rnd.title")}</p>
            <p className="cr-section-desc">{t("clinicalresearch.rnd.desc")}</p>
          </div>
          <div className="cr-compare cr-rnd">
            {rndItems.map((key) => (
              <div className="cr-compare-col cr-compare-new" key={key}>
                <p className="cr-compare-heading">
                  {t(`clinicalresearch.rnd.items.${key}.title`)}
                </p>
                <ul className="cr-compare-list">
                  {(t(`clinicalresearch.rnd.items.${key}.points`, { returnObjects: true }) || []).map(
                    (point, i) => (
                      <li key={i}>{point}</li>
                    )
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="cr-cta">
          <p className="cr-cta-title">{t("clinicalresearch.cta.title")}</p>
          <p className="cr-cta-subtitle">{t("clinicalresearch.cta.subtitle")}</p>
          <a className="cr-cta-email" href={`mailto:${t("clinicalresearch.cta.email")}`}>
            {t("clinicalresearch.cta.email")}
          </a>
          <div className="cr-cta-buttons">
            <button className="cr-cta-btn cr-cta-btn-line" onClick={() => goto("/whoweare#cases")}>
              {t("clinicalresearch.cta.cases")}
            </button>
            <button className="cr-cta-btn" onClick={() => goto("/contactus")}>
              {t("clinicalresearch.cta.contact")}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ClinicalResearch;
